/**
 * Voice activity: the RMS envelope, the speech mask, bursts, and edge snapping.
 *
 * THIS IS LIVE CODE ON THE UI PATH. It was extracted from
 * `refineBoundaries.ts`, whose remaining half is the legacy repair stage and
 * now lives in `services/legacy/`. The split exists because these four
 * functions have consumers that have nothing to do with the legacy pipeline:
 *
 *   - `components/taskAudio.ts` builds the waveform drag magnets on CorrectPage
 *     and CompletedPage from `computeSpeechEnvelope`,
 *   - `pipeline/alignedTiling.ts` decides what unlabelled audio IS (noise vs
 *     silence) from the same mask,
 *   - the alignment score and burst re-segmentation both measure against
 *     `findSpeechBursts`.
 *
 * Quarantining the whole file would have taken all of that with it.
 *
 * Everything here is a pure function over Float32Arrays: no db, no audio
 * context, no network.
 */

/** Envelope geometry. The rest of the old DEFAULTS block went with stage 2–5. */
const DEFAULTS = {
  hopSec: 0.01,
  winSec: 0.025,
} as const;

/* ------------------------------------------------------------------ */
/* Envelope and voice activity                                         */
/* ------------------------------------------------------------------ */

export interface SpeechEnvelope {
  /** Per-frame RMS. */
  rms: Float32Array;
  /** Per-frame speech flag, same length as {@link rms}. */
  mask: Uint8Array;
  /** Seconds per frame. */
  hopSec: number;
  /** Indices of the speech frames, ascending. */
  speechFrames: Int32Array;
}

/**
 * Short-window RMS plus a hysteresis speech mask.
 *
 * Thresholds are derived from the file's own level distribution (10th and 99th
 * percentile) rather than an absolute dBFS value, so a quiet phone recording
 * and a hot studio take get the same treatment. Two thresholds rather than one
 * stops a word from being chopped into fragments every time its energy dips
 * through a stop consonant.
 */
export function computeSpeechEnvelope(
  mono: Float32Array,
  sampleRate: number,
  options: { hopSec?: number; winSec?: number } = {},
): SpeechEnvelope {
  const hopSec = options.hopSec ?? DEFAULTS.hopSec;
  const winSec = options.winSec ?? DEFAULTS.winSec;
  const hop = Math.max(1, Math.round(sampleRate * hopSec));
  const win = Math.max(hop, Math.round(sampleRate * winSec));
  const frames = Math.max(1, Math.floor((mono.length - win) / hop) + 1);

  const rms = new Float32Array(frames);
  for (let i = 0; i < frames; i++) {
    const from = i * hop;
    const to = Math.min(mono.length, from + win);
    let sum = 0;
    for (let j = from; j < to; j++) {
      const v = mono[j];
      sum += v * v;
    }
    rms[i] = Math.sqrt(sum / Math.max(1, to - from));
  }

  // percentile thresholds against the file's own dynamic range
  const sorted = Float32Array.from(rms).sort();
  const at = (p: number) => sorted[Math.min(sorted.length - 1, Math.max(0, Math.floor(sorted.length * p)))];
  const floor = at(0.1);
  const peak = at(0.99);
  const range = Math.max(1e-6, peak - floor);
  const hi = floor + range * 0.15;
  const lo = floor + range * 0.06;

  const mask = new Uint8Array(frames);
  let on = false;
  for (let i = 0; i < frames; i++) {
    if (!on && rms[i] > hi) on = true;
    else if (on && rms[i] < lo) on = false;
    mask[i] = on ? 1 : 0;
  }
  // Hysteresis only latches on at the HIGH threshold, so the quiet onset of a
  // word (the ramp from lo up to hi) is left outside the mask. Walk each run's
  // leading edge back down to the low threshold to recover it.
  for (let i = frames - 1; i > 0; i--) {
    if (mask[i] && !mask[i - 1] && rms[i - 1] > lo) mask[i - 1] = 1;
  }

  let count = 0;
  for (let i = 0; i < frames; i++) if (mask[i]) count++;
  const speechFrames = new Int32Array(count);
  for (let i = 0, k = 0; i < frames; i++) if (mask[i]) speechFrames[k++] = i;

  return { rms, mask, hopSec, speechFrames };
}

/** One continuous stretch of speech, bounded by real silence. */
export interface Burst {
  startSec: number;
  endSec: number;
}

/** Silence shorter than this does not end a burst (it is a stop consonant). */
const GAP_CLOSE_SEC = 0.12;

/** Speech runs shorter than this are envelope noise, not a burst. */
const MIN_BURST_SEC = 0.06;

/**
 * Collapse the VAD mask into speech bursts.
 *
 * A burst is what a human would call "one uninterrupted stretch of talking":
 * gaps shorter than {@link GAP_CLOSE_SEC} are closed, because the silence inside
 * a stop consonant is not a word boundary, and stray one-frame blips are
 * dropped. These are the units the first pass mis-allocates labels within, so
 * they are the units stages 5 and the QA re-segmenter re-cut.
 *
 * Lives here, next to the envelope it reads, so that every stage which talks
 * about "a burst" means the identical thing — burst re-segmentation, the
 * alignment score and the onset stage all import this one implementation.
 */
export function findSpeechBursts(
  env: SpeechEnvelope,
  options: { gapSec?: number; minBurstSec?: number } = {},
): Burst[] {
  const gapSec = options.gapSec ?? GAP_CLOSE_SEC;
  const minBurstSec = options.minBurstSec ?? MIN_BURST_SEC;
  const { mask, hopSec } = env;

  const raw: Burst[] = [];
  let runStart = -1;
  for (let i = 0; i < mask.length; i++) {
    if (mask[i]) {
      if (runStart < 0) runStart = i;
    } else if (runStart >= 0) {
      raw.push({ startSec: runStart * hopSec, endSec: i * hopSec });
      runStart = -1;
    }
  }
  if (runStart >= 0) raw.push({ startSec: runStart * hopSec, endSec: mask.length * hopSec });

  const merged: Burst[] = [];
  for (const b of raw) {
    const last = merged[merged.length - 1];
    if (last && b.startSec - last.endSec < gapSec) last.endSec = b.endSec;
    else merged.push({ ...b });
  }
  return merged.filter((b) => b.endSec - b.startSec >= minBurstSec);
}

/* ------------------------------------------------------------------ */
/* Local snap                                                          */
/* ------------------------------------------------------------------ */

/**
 * Nearest frame where the speech mask rises (`rising`) or falls, within radius.
 *
 * Exported because burst re-segmentation needs exactly this behaviour — the same
 * envelope, the same edge definition — on the boundaries it rebuilds. Returns
 * `timeSec` unchanged when there is no edge in range, which is the common case
 * INSIDE a burst and is what makes the call safe to apply unconditionally.
 */
export function snapToEdge(
  timeSec: number,
  env: SpeechEnvelope,
  radiusSec: number,
  rising: boolean,
): number {
  const mask = env.mask;
  const hop = env.hopSec;
  const centre = Math.round(timeSec / hop);
  const radius = Math.max(1, Math.round(radiusSec / hop));
  let bestFrame = -1;
  let bestDist = Infinity;

  const from = Math.max(1, centre - radius);
  const to = Math.min(mask.length - 1, centre + radius);
  for (let i = from; i <= to; i++) {
    const isEdge = rising ? mask[i] === 1 && mask[i - 1] === 0 : mask[i - 1] === 1 && mask[i] === 0;
    if (!isEdge) continue;
    const dist = Math.abs(i - centre);
    if (dist < bestDist) {
      bestDist = dist;
      bestFrame = i;
    }
  }
  return bestFrame < 0 ? timeSec : bestFrame * hop;
}
